import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { ReactComponent as Moon } from '../../../assets/icons/moon.svg'
import { ReactComponent as Save } from '../../../assets/icons/save.svg'
import { ReactComponent as Sun } from '../../../assets/icons/sunny.svg'
import { INavigation } from '../MonacoSection'

import { joinClassNames } from '../../../utils/styles'
import styles from './monaco-page-header.module.scss'

interface IMonacoPageHeader {
    title?: string
    navigation?: INavigation[]
    currentNavigationOption?: INavigation
    value: string | undefined
    handleSave: (data: string) => Promise<void>
    isThemeDark: boolean
    setIsThemeDark: React.Dispatch<React.SetStateAction<boolean>>
    children?: React.ReactNode
    rawData?: string
    handleSelect?: (value: string) => void
    hideSaved?: boolean
}

function MonacoPageHeader(props: IMonacoPageHeader) {
    const {
        title,
        navigation,
        currentNavigationOption,
        value,
        handleSave,
        isThemeDark,
        setIsThemeDark,
        children,
        rawData,
        handleSelect,
        hideSaved,
    } = props

    const params = useParams()

    const { t } = useTranslation()
    const navigate = useNavigate()

    const [savedValue, setSavedValue] = useState(value)

    useEffect(() => {
        setSavedValue(rawData)
    }, [rawData])

    const handleHeaderSave = () => {
        if (value === undefined) return

        handleSave(value).then(() => {
            setSavedValue(value)
        })
    }

    const isSaved = savedValue === value

    const actualValue = (params['*'] || '').substring(
        (params['*'] || '').lastIndexOf('/') + 1
    )

    const isActualOptionExists = navigation?.find(
        (option) => option._id === actualValue
    )

    return (
        <header className={styles.monacoPageHeader}>
            <div className={styles.monacoPageHeader__data}>
                {title ? (
                    <div className={styles.monacoPageHeader__title}>
                        {title}
                    </div>
                ) : null}
                {navigation && navigation.length ? (
                    <select
                        className={styles.monacoPageHeader__select}
                        value={actualValue}
                        onChange={(e) =>
                            handleSelect
                                ? handleSelect(e.target.value)
                                : navigate(`./../${e.target.value}`)
                        }
                    >
                        {!isActualOptionExists && currentNavigationOption ? (
                            <option
                                key={currentNavigationOption._id}
                                value={currentNavigationOption._id}
                            >
                                {currentNavigationOption.name}
                            </option>
                        ) : null}
                        {navigation.map((item) => (
                            <option key={item._id} value={item._id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                ) : null}
                <button
                    className={styles.monacoPageHeader__saveBtn}
                    onClick={handleHeaderSave}
                >
                    <Save className={styles.monacoPageHeader__icon} />
                    {t('save')}
                </button>
                {!hideSaved && (
                    <div
                        className={joinClassNames(
                            styles.monacoPageHeader__status
                        )}
                    >
                        {isSaved ? (
                            <>
                                <span
                                    className={joinClassNames(
                                        styles.monacoPageHeader__dot,
                                        styles.monacoPageHeader__dotGreen
                                    )}
                                />
                                {t('saved')}
                            </>
                        ) : (
                            <>
                                <span
                                    className={styles.monacoPageHeader__dot}
                                />
                                {t('unsaved')}
                            </>
                        )}
                    </div>
                )}
                {children}
            </div>
            <div className={styles.monacoPageHeader__theme}>
                <button
                    className={joinClassNames(
                        styles.monacoPageHeader__themeBtn,
                        isThemeDark && styles.monacoPageHeader__themeBtnActive
                    )}
                    onClick={() => setIsThemeDark(true)}
                >
                    <Moon className={styles.monacoPageHeader__themeIcon} />
                </button>
                <button
                    className={joinClassNames(
                        styles.monacoPageHeader__themeBtn,
                        !isThemeDark && styles.monacoPageHeader__themeBtnActive
                    )}
                    onClick={() => setIsThemeDark(false)}
                >
                    <Sun className={styles.monacoPageHeader__themeIcon} />
                </button>
            </div>
        </header>
    )
}

export default MonacoPageHeader
